import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReviewSubmissionModal from '../review-analytics-success-metrics-dashboard/components/ReviewSubmissionModal';
import { useAuth } from '../../contexts/AuthContext';


const PricingPlansSubscriptionCenter = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [employeeCount, setEmployeeCount] = useState(50);
  const [showComparison, setShowComparison] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedPlanForPricing, setSelectedPlanForPricing] = useState(null);

  // Pricing plans
  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      price: 500,
      yearlyPrice: 5000,
      employeeLimit: '1-10 employees',
      description: 'Essential compliance for small teams',
      color: 'blue',
      popular: false,
      features: [
        { name: 'Employment Act Compliance Monitoring', included: true },
        { name: 'Basic Payroll Processing', included: true },
        { name: 'Leave Management', included: true },
        { name: 'Employee Self-Service Portal', included: true },
        { name: 'Email Support', included: true },
        { name: 'NASFUND Filing Automation', included: false },
        { name: 'Advanced Analytics', included: false },
        { name: 'Custom Reports', included: false },
        { name: 'API Access', included: false },
        { name: 'Dedicated Account Manager', included: false }
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 1500,
      yearlyPrice: 15000,
      employeeLimit: '11-100 employees',
      description: 'Complete HR automation for growing organizations',
      color: 'emerald',
      popular: true,
      features: [
        { name: 'Employment Act Compliance Monitoring', included: true },
        { name: 'Advanced Payroll Processing', included: true },
        { name: 'Leave Management', included: true },
        { name: 'Employee Self-Service Portal', included: true },
        { name: 'Priority Email & Phone Support', included: true },
        { name: 'NASFUND Filing Automation', included: true },
        { name: 'Advanced Analytics & Dashboards', included: true },
        { name: 'Custom Reports', included: true },
        { name: 'API Access', included: false },
        { name: 'Dedicated Account Manager', included: false }
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: null,
      yearlyPrice: null,
      employeeLimit: '100+ employees',
      description: 'Tailored solutions for large departments',
      color: 'purple',
      popular: false,
      customPricing: 'Minimum K5,000+',
      features: [
        { name: 'Employment Act Compliance Monitoring', included: true },
        { name: 'Enterprise Payroll Processing', included: true },
        { name: 'Leave Management', included: true },
        { name: 'Employee Self-Service Portal', included: true },
        { name: '24/7 Priority Support', included: true },
        { name: 'NASFUND Filing Automation', included: true },
        { name: 'Advanced Analytics & Dashboards', included: true },
        { name: 'Unlimited Custom Reports', included: true },
        { name: 'Full API Access', included: true },
        { name: 'Dedicated Account Manager', included: true }
      ]
    }
  ];

  // Calculate savings
  const calculateSavings = (plan) => {
    if (!plan?.price) return null;
    const monthlyCost = plan?.price;
    const yearlyCost = plan?.yearlyPrice;
    const monthlyEquivalent = monthlyCost * 12;
    const savings = monthlyEquivalent - yearlyCost;
    const savingsPercent = ((savings / monthlyEquivalent) * 100)?.toFixed(0);
    return { savings, savingsPercent };
  };

  // ROI calculation
  const calculateROI = () => {
    const currentManualCost = employeeCount * 15; // K15 per employee/month manual processing
    const automationSavings = employeeCount * 4.3; // K4.3 per employee/month automation savings
    const annualSavings = automationSavings * 12;
    return {
      monthlySavings: automationSavings?.toFixed(0),
      annualSavings: annualSavings?.toFixed(0),
      currentCost: currentManualCost?.toFixed(0)
    };
  };

  const roiData = calculateROI();

  // Testimonials - Open for future client reviews
  const testimonials = [];

  // FAQ data
  const faqs = [
    {
      question: 'What is included in the free trial?',
      answer: 'All plans include a 14-day free trial with full access to all features in your selected tier. No credit card required to start. You can import your employee data, test payroll processing, and explore all compliance features risk-free.'
    },
    {
      question: 'How does PNG Employment Act compliance work?',
      answer: 'Our system automatically monitors your HR practices against PNG Employment Act 1978 requirements, including minimum wage compliance, leave entitlements, overtime calculations, and termination procedures. You receive real-time alerts for any potential violations with guided remediation steps.'
    },
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer: 'Yes, you can change your plan at any time. Upgrades take effect immediately with prorated billing. Downgrades take effect at the start of your next billing cycle. Your data is preserved during all plan changes.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept BSP bank transfers, credit cards (Visa, Mastercard), and government purchase orders. For Enterprise plans, we offer flexible payment terms including quarterly and annual invoicing.'
    },
    {
      question: 'How long does implementation take?',
      answer: 'Most organizations complete setup in under 15 minutes using our Quick Setup Wizard. For Enterprise clients with complex requirements, our implementation team provides hands-on support with typical deployment in 1-2 weeks.'
    },
    {
      question: 'Is my data secure and compliant?',
      answer: 'Yes. We use bank-grade encryption and comply with PNG data protection requirements. All data is stored in secure cloud servers with daily backups and 99.9% uptime guarantee.'
    },
    {
      question: 'Do you offer discounts for government departments?',
      answer: 'Yes. PNG government departments receive special pricing with bulk discounts. Contact our sales team for a customized quote based on your employee count and requirements.'
    },
    {
      question: 'What support is included?',
      answer: 'Basic plans include email support (24-hour response time). Professional plans add phone support (4-hour response). Enterprise plans include 24/7 priority support with a dedicated account manager and quarterly business reviews.'
    },
    {
      question: 'Is there a contract or commitment required?',
      answer: 'No contracts required. Our service operates on a pay-as-you-go basis. If the service doesn\'t work for you, simply stop using it and you won\'t be charged again. You have complete flexibility to cancel anytime.'
    }
  ];

  const handleStartTrial = (plan) => {
    setSelectedPlan(plan);
    navigate('/quick-setup-configuration-wizard');
  };

  const handleGetPricing = (plan) => {
    setSelectedPlanForPricing(plan);
    setShowPricingModal(true);
  };

  const handleContactSales = () => {
    window.location.href = 'mailto:admin@hrpacificrbs.com?subject=Enterprise Plan Inquiry';
  };

  const handleProceedToCheckout = () => {
    setShowPricingModal(false);
    navigate('/subscription-checkout-payment-portal', { 
      state: { 
        selectedPlan: selectedPlanForPricing,
        billingCycle 
      } 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Transparent Pricing for PNG Organizations
            </h1>
            <p className="text-xl text-emerald-50 mb-8">
              Choose the plan that fits your organization. All plans include PNG Employment Act compliance.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-lg p-1 border border-white/20">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  billingCycle === 'monthly' ?'bg-white text-emerald-700 shadow-sm' :'text-white hover:text-emerald-100'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  billingCycle === 'yearly' ?'bg-white text-emerald-700 shadow-sm' :'text-white hover:text-emerald-100'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs bg-emerald-400 text-emerald-900 px-2 py-0.5 rounded-full">
                  Save 17%
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {plans?.map((plan) => {
              const savings = calculateSavings(plan);
              return (
                <div
                  key={plan?.id}
                  className={`relative bg-white dark:bg-slate-900 border-2 rounded-2xl p-8 transition-all duration-300 hover:shadow-xl ${
                    plan?.popular
                      ? 'border-emerald-500 shadow-lg scale-105'
                      : 'border-gray-200 dark:border-slate-700 hover:border-emerald-300'
                  }`}
                >
                  {plan?.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-emerald-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                        Most Popular
                      </span>
                    </div>
                  )}

                  {/* Plan Header */}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {plan?.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {plan?.employeeLimit}
                    </p>

                    {/* Price */}
                    <div className="mb-4">
                      {plan?.price ? (
                        <>
                          <div className="flex items-baseline justify-center gap-2">
                            <span className="text-4xl font-bold text-gray-900 dark:text-white">
                              K{billingCycle === 'monthly' ? plan?.price : Math.round(plan?.yearlyPrice / 12)}
                            </span>
                            <span className="text-gray-600 dark:text-gray-400">/month</span>
                          </div>
                          {billingCycle === 'yearly' && savings && (
                            <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2">
                              Save K{savings?.savings?.toLocaleString()} ({savings?.savingsPercent}%) annually
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="text-3xl font-bold text-gray-900 dark:text-white">
                          {plan?.customPricing || 'Custom'}
                        </div>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {plan?.description}
                    </p>
                  </div>

                  {/* Features List */}
                  <div className="space-y-3 mb-8">
                    {plan?.features?.slice(0, 5)?.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Icon
                          name={feature?.included ? 'CheckCircle' : 'X'}
                          size={20}
                          className={feature?.included ? 'text-emerald-600' : 'text-gray-300 dark:text-gray-600'}
                        />
                        <span className={`text-sm ${
                          feature?.included
                            ? 'text-gray-700 dark:text-gray-300' :'text-gray-400 dark:text-gray-600'
                        }`}>
                          {feature?.name}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  {plan?.price ? (
                    <div className="space-y-2">
                      <Button
                        className="w-full"
                        variant={plan?.popular ? 'default' : 'outline'}
                        size="lg"
                        iconName="ArrowRight"
                        onClick={() => handleStartTrial(plan)}
                      >
                        Start Free Trial
                      </Button>
                      <Button
                        className="w-full"
                        variant="ghost"
                        size="sm"
                        iconName="CreditCard"
                        onClick={() => handleGetPricing(plan)}
                      >
                        Get Pricing Info
                      </Button>
                    </div>
                  ) : (
                    <Button
                      className="w-full"
                      variant="outline"
                      size="lg"
                      iconName="Mail"
                      onClick={handleContactSales}
                    >
                      Contact Sales
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {/* View Full Comparison */}
          <div className="text-center mt-12">
            <Button
              variant="ghost"
              iconName={showComparison ? 'ChevronUp' : 'ChevronDown'}
              onClick={() => setShowComparison(!showComparison)}
            >
              {showComparison ? 'Hide' : 'View'} Full Feature Comparison
            </Button>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      {showComparison && (
        <section className="py-16 bg-white dark:bg-slate-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                Complete Feature Comparison
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-200 dark:border-slate-700">
                      <th className="text-left py-4 px-4 text-gray-900 dark:text-white font-semibold">
                        Feature
                      </th>
                      {plans?.map((plan) => (
                        <th key={plan?.id} className="text-center py-4 px-4">
                          <span className="text-gray-900 dark:text-white font-semibold">
                            {plan?.name}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plans?.[0]?.features?.map((feature, index) => (
                      <tr key={index} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800">
                        <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300">
                          {feature?.name}
                        </td>
                        {plans?.map((plan) => (
                          <td key={plan?.id} className="text-center py-4 px-4">
                            {plan?.features?.[index]?.included ? (
                              <Icon name="Check" size={20} className="text-emerald-600 mx-auto" />
                            ) : (
                              <Icon name="Minus" size={20} className="text-gray-300 dark:text-gray-600 mx-auto" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PNG Government Pricing Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-emerald-50 dark:from-slate-800 dark:to-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-4 py-2 rounded-full mb-4">
                <Icon name="Building2" size={16} />
                <span className="text-sm font-semibold">Government Department Pricing</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Special Rates for PNG Public Sector
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Bulk discounts and flexible payment terms for government organizations
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Bulk Discounts */}
              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                    <Icon name="Users" size={24} className="text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Bulk Discounts
                  </h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">100-500 employees</span>
                    <span className="font-semibold text-emerald-600">15% off</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">500-1,000 employees</span>
                    <span className="font-semibold text-emerald-600">25% off</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">1,000+ employees</span>
                    <span className="font-semibold text-emerald-600">35% off</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Button
                size="lg"
                iconName="Mail"
                onClick={handleContactSales}
              >
                Request Government Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Integration */}
      <section className="py-16 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Cost Savings vs Manual HR Processes
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                See how automation reduces your HR operational costs
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-slate-800 dark:to-slate-900 border border-emerald-200 dark:border-slate-700 rounded-2xl p-8">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Current Manual Cost</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    K{parseInt(roiData?.currentCost)?.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">per month</p>
                </div>

                <div className="bg-emerald-600 rounded-xl p-6 text-white">
                  <p className="text-sm text-emerald-100 mb-2">Monthly Savings</p>
                  <p className="text-3xl font-bold">
                    K{parseInt(roiData?.monthlySavings)?.toLocaleString()}
                  </p>
                  <p className="text-xs text-emerald-200 mt-1">with automation</p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Annual Savings</p>
                  <p className="text-3xl font-bold text-emerald-600">
                    K{parseInt(roiData?.annualSavings)?.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">projected</p>
                </div>
              </div>

              <div className="text-center">
                <Button
                  variant="outline"
                  iconName="Calculator"
                  onClick={() => navigate('/marketing-website-landing-page')}
                >
                  Use Full ROI Calculator
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gray-50 dark:bg-slate-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-12 text-center">
              What Our Customers Say
            </h2>

            {testimonials?.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-8">
                {testimonials?.map((testimonial, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                        <Icon name="Building2" size={20} className="text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {testimonial?.department}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {testimonial?.plan} Plan
                        </p>
                      </div>
                    </div>

                    <blockquote className="text-gray-700 dark:text-gray-300 mb-4 italic">
                      "{testimonial?.quote}"
                    </blockquote>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-slate-700">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm">
                          {testimonial?.name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {testimonial?.position}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-600 dark:text-gray-400">Annual Savings</p>
                        <p className="text-lg font-bold text-emerald-600">
                          {testimonial?.savings}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-900 border-2 border-dashed border-gray-300 dark:border-slate-700 rounded-xl p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="MessageSquare" size={32} className="text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Client Testimonials Coming Soon
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-6">
                  We're collecting feedback from our clients. After using our system, clients will have the opportunity to share their experiences and success stories here.
                </p>
                {isAuthenticated && (
                  <Button
                    onClick={() => setShowReviewModal(true)}
                    iconName="Star"
                    className="mx-auto"
                  >
                    Submit Your Review
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Everything you need to know about our pricing and plans
              </p>
            </div>

            <div className="space-y-4">
              {faqs?.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <span className="font-semibold text-gray-900 dark:text-white pr-4">
                      {faq?.question}
                    </span>
                    <Icon
                      name={expandedFaq === index ? 'ChevronUp' : 'ChevronDown'}
                      size={20}
                      className="text-gray-600 dark:text-gray-400 flex-shrink-0"
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-5 pb-5">
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {faq?.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-emerald-50 mb-8">
              Start your 14-day free trial today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto"
                iconName="Zap"
                onClick={() => navigate('/quick-setup-configuration-wizard')}
              >
                Start Free Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 w-full sm:w-auto"
                iconName="Mail"
                onClick={handleContactSales}
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Review Submission Modal */}
      <ReviewSubmissionModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        onSuccess={() => {
          console.log('Review submitted successfully');
        }}
      />

      {/* Pricing Information Modal */}
      {showPricingModal && selectedPlanForPricing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedPlanForPricing?.name} Plan Pricing
                </h2>
                <button
                  onClick={() => setShowPricingModal(false)}
                  className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} className="text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Pricing Summary */}
              <div className="bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-emerald-200 dark:border-emerald-800">
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Selected Plan</p>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      K{billingCycle === 'monthly' ? selectedPlanForPricing?.price : Math.round(selectedPlanForPricing?.yearlyPrice / 12)}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">/month</span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2">
                      Billed annually at K{selectedPlanForPricing?.yearlyPrice?.toLocaleString()} (Save 17%)
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-4">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Employee Limit</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedPlanForPricing?.employeeLimit}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-4">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Billing Cycle</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white capitalize">{billingCycle}</p>
                  </div>
                </div>
              </div>

              {/* What's Included */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">What's Included</h3>
                <div className="space-y-3">
                  {selectedPlanForPricing?.features?.filter(f => f?.included)?.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Icon name="CheckCircle" size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{feature?.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Payment Options</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      We accept BSP bank transfers, credit cards (Visa, Mastercard), and government purchase orders. 
                      All plans include a 14-day free trial with no credit card required.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  className="flex-1"
                  size="lg"
                  iconName="Zap"
                  onClick={() => {
                    setShowPricingModal(false);
                    handleStartTrial(selectedPlanForPricing);
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  className="flex-1"
                  variant="outline"
                  size="lg"
                  iconName="CreditCard"
                  onClick={handleProceedToCheckout}
                >
                  Proceed to Payment
                </Button>
              </div>

              <div className="text-center">
                <button
                  onClick={handleContactSales}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Have questions? Contact our sales team
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPlansSubscriptionCenter;